import { UploadError } from '@/shared/utils/run-media-upload/run-media-upload.util';
import { type UploadFailureReason } from '@/shared/utils/run-media-upload/run-media-upload.types';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';
import { NETWORK_ERROR_CODE } from '@/shared/utils/transform-error/transform-error.types';

/**
 * i18n keys in the `uiKit` namespace, under `upload.*`.
 *
 * `canceled` maps to **null**, not to a key: the operator pressed Cancel, and a
 * line of red text under an upload they stopped themselves reads as a failure
 * they now have to think about.
 */
const REASON_KEYS = {
  wrongType: 'upload.wrongType',
  tooLarge: 'upload.tooLarge',
  expired: 'upload.expired',
  network: 'upload.network',
  failed: 'upload.failed',
  canceled: null,
} as const satisfies Record<UploadFailureReason, string | null>;

/**
 * The api error codes worth their own line of copy. Anything else is a generic
 * failure — a wall of server codes translated one by one is a catalogue nobody
 * maintains, and most of them mean the same thing to the person holding a photo.
 */
const API_CODE_KEYS: Readonly<Record<string, string>> = {
  media_mime_not_allowed: REASON_KEYS.wrongType,
  media_too_large: REASON_KEYS.tooLarge,
  media_singleton_exists: 'upload.singleton',
  media_not_an_image: 'upload.unreadable',
  media_not_a_document: 'upload.unreadable',
};

function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'code' in error && 'status' in error;
}

/**
 * The one line to show under a failed upload — or `null` for the one outcome
 * that needs no line at all.
 *
 * Both halves of the pipeline can fail and they fail differently: the transfer
 * itself raises an `UploadError` with a reason, while presign and finalize raise
 * an `ApiError` with a server code. One function so the uploader has a single
 * place to ask, and so a failure never renders as a raw code.
 */
export function classifyUploadError(error: unknown): string | null {
  if (error instanceof UploadError) {
    return REASON_KEYS[error.reason];
  }

  if (isApiError(error)) {
    if (error.code === NETWORK_ERROR_CODE) {
      return REASON_KEYS.network;
    }

    return API_CODE_KEYS[error.code] ?? REASON_KEYS.failed;
  }

  return REASON_KEYS.failed;
}
